import {
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Select as ShadSelect
} from '@/components/ui/Select';
import { SearchInput } from '@/components/wm';
import { cn } from '@/utils';
import * as React from 'react';

export const Select = React.forwardRef(function Select(
    {
        className,
        onChange,
        options = [],
        placeholder = i18next.t('ui.please_select_option'),
        value,
        sort = null, // accepts: asc, desc, null
        search = 50, // accepts: true, false, or int (min items)
        ...props
    },
    ref
) {
    const [searchTerm, setSearchTerm] = React.useState('');

    const baseOptions = React.useMemo(() => {
        return Array.isArray(options)
            ? options
            : Object.entries(options).map(([key, val]) => ({
                value: key,
                label: val.label
            }));
    }, [options]);

    const isSearchActive = React.useMemo(() => {
        if (typeof search === 'number') {
            return baseOptions.length >= search;
        }
        return !!search;
    }, [search, baseOptions.length]);

    const normalizedOptions = React.useMemo(() => {
        let filtered = baseOptions.filter(opt => {
            // ALWAYS include the currently selected value to prevent Radix from losing context
            if (opt.value === value) {
                return true;
            }

            // Only filter if search is "Active" (based on boolean or count) AND there is text
            if (isSearchActive && searchTerm) {
                return opt.label.toLowerCase().includes(searchTerm.toLowerCase());
            }
            return true;
        });

        if (sort) {
            filtered.sort((a, b) => {
                const labelA = a.label.toLowerCase();
                const labelB = b.label.toLowerCase();
                if (sort === 'asc') {
                    return labelA > labelB ? 1 : -1;
                }
                if (sort === 'desc') {
                    return labelA < labelB ? 1 : -1;
                }
                return 0;
            });
        }

        return filtered;
    }, [baseOptions, searchTerm, sort, isSearchActive, value]);

    return (
        <ShadSelect
            onValueChange={onChange}
            value={value}
            {...props}
            onOpenChange={open => !open && setSearchTerm('')}
        >
            <SelectTrigger className={cn(
                'tw-ml-0',
                'focus:tw-border-slate-400 focus:!tw-ring-0 focus:!tw-ring-offset-0',
                'focus-visible:tw-border-slate-400 focus-visible:!tw-ring-0 focus-visible:!tw-ring-offset-0',
                'data-[state=open]:tw-border-slate-400 data-[state=open]:!tw-ring-0 data-[state=open]:!tw-ring-offset-0',
                className
            )} ref={ref}>
                <SelectValue placeholder={placeholder}/>
            </SelectTrigger>

            <SelectContent className="tw-p-0">
                {isSearchActive && (
                    <div className="tw-max-w-full tw-p-4 tw-sticky tw-top-0 tw-bg-popover tw-z-10">
                        <SearchInput
                            className="tw-w-full"
                            inputClassName='tw-max-w-[90%] tw-h-6 tw-mr-2 tw-border tw-border-solid tw-border-input tw-py-0 tw-pl-8'
                            onChange={e => setSearchTerm(e.target.value)}
                            onClick={e => e.stopPropagation()}
                            onKeyDown={e => e.stopPropagation()}
                            placeholder={i18next.t('glossary.search').capitalize()}
                            value={searchTerm}
                        />
                    </div>
                )}

                {normalizedOptions.length > 0 ? (
                    normalizedOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                        </SelectItem>
                    ))
                ) : (
                    <div className="tw-py-2 tw-px-8 tw-text-xs tw-text-gray-400">
                        {i18next.t('ui.no_options_found')}
                    </div>
                )}
            </SelectContent>
        </ShadSelect>
    );
});
